import { ArrowsUpDownIcon } from '@heroicons/react/16/solid';
import { type ReactNode } from 'react';

import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/catalyst/badge';

/**
 * Props for ColumnFilterHeader component
 */
interface ColumnFilterHeaderProps {
  /** Column header text or ReactNode for custom icons */
  header: string | ReactNode;
  /** Number of active filters */
  filterCount: number;
  /** Current sort direction (null if not sorted) */
  sortDirection: 'asc' | 'desc' | null;
  /** Whether the column has active filters */
  isFiltered: boolean;
  /** Invert arrow direction for semantic display (e.g., ↑ = newest for dates) */
  invertArrows?: boolean;
  /** Whether the column is sortable (shows subtle indicator when not sorted) */
  sortable?: boolean;
}

/**
 * Header component for unified column filter/sort dropdown
 *
 * Displays:
 * - Column header text
 * - Sort direction indicator (↑ or ↓) when sorted
 * - Filter count badge when filtered
 *
 * Visual states:
 * - Default: Header only (clean, minimal)
 * - Filtered: Header + badge (blue, active state)
 * - Sorted: Header + sort arrow (blue text)
 * - Filtered + Sorted: Header + badge + sort arrow (blue text)
 *
 * @example
 * ```tsx
 * <ColumnFilterHeader
 *   header="Firm"
 *   filterCount={2}
 *   sortDirection="asc"
 *   isFiltered={true}
 * />
 * // Renders: "Firm ↑ 2"
 * ```
 */
export const ColumnFilterHeader = ({
  header,
  filterCount,
  sortDirection,
  isFiltered,
  invertArrows = false,
  sortable = false,
}: ColumnFilterHeaderProps) => {
  // Check if header is an emoji (single character that's not a letter/number)
  // ReactNode headers are treated as icons (similar to emoji styling)
  const isStringHeader = typeof header === 'string';
  const isEmoji =
    isStringHeader && header.length <= 2 && /[\p{Emoji}]/u.test(header);
  const isIconHeader = !isStringHeader || isEmoji;

  // When inverted, flip the arrow display (semantic: ↑ = newest for dates)
  const displayArrow = sortDirection
    ? invertArrows
      ? sortDirection === 'desc'
        ? '↑'
        : '↓'
      : sortDirection === 'asc'
        ? '↑'
        : '↓'
    : null;

  return (
    <div className="flex items-center gap-1">
      {/* Column header text or icon */}
      <span
        className={cn(
          'font-semibold text-black dark:text-white',
          isIconHeader
            ? 'text-xl leading-none flex items-center'
            : 'text-[12px] leading-6',
        )}
      >
        {header}
      </span>

      {/* Sort direction indicator (show arrow when sorted, or sortable icon when sortable but not sorted) */}
      {displayArrow ? (
        <span
          className="text-blue-600 dark:text-blue-400 text-xs"
          aria-label={`Sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
        >
          {displayArrow}
        </span>
      ) : (
        sortable && (
          <ArrowsUpDownIcon
            className="h-3 w-3 text-zinc-400 dark:text-zinc-500"
            aria-label="Sortable column"
          />
        )
      )}

      {/* Filter count badge (only show when filtered) */}
      {isFiltered && filterCount > 0 && (
        <Badge
          color="blue"
          aria-label={`${filterCount} filters active`}
          className="!text-[10px] !px-1 !py-0 !leading-4"
        >
          {filterCount}
        </Badge>
      )}
    </div>
  );
};
